import { FC, useCallback, useContext, useState } from 'react';
import { TableNew, TableRecord } from '@dynamika-shared/ui/TableNew/TableNew';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { useNavigate } from 'react-router-dom';
import { useParams } from '@hooks/useParams';
import { VisitConfigQuestionnaires } from '@dynamika-shared/types/visits';
import { twMerge } from '@utils/twMerge';
import { transformVisits } from 'src/pages/ClinicalTrial/ClinicalTrialForms/ClinicalTrialFormsAssigned/transformVisits';
import { uiControlSchemas } from 'src/pages/ClinicalTrial/uiControlSchemaByStatus/schemas';
import { StudyStatusEnum } from '@dynamika-shared/types/status';
import { ClinicalTrialType } from '@dynamika-shared/types/clinicalTrials';
import { TemplateContext } from 'src/pages/ClinicalTrial/ClinicalTrialForms/ClinicalTrialForms';
import { routesPath } from '@router/routes';
import { transformExpandedVisit } from 'src/pages/ClinicalTrial/ClinicalTrialForms/ClinicalTrialFormsAssigned/transformExpandedVisit';
import { useQuestionnairesSlice } from '@restore/questionnaires/questionnaires.slice';
import { useClinicalTrialFormSlice } from '@restore/clinicalTrialForms/clinicalTrialForms.slice';
import { useAuthSlice } from '@restore/auth/auth.slice';
import { UserRoles } from '@dynamika-shared/types/auth';

const visitsColumns = [
  {
    title: 'Name',
    dataIndex: 'name',
    className: '!py-1 col-1',
  },
  {
    className: 'w-[30%]',
  },
  {
    title: <div className={'text-right'}>Action</div>,
    dataIndex: 'action',
    className: '!pr-[2.7rem]',
  },
];

type ClinicalTrialFormsAssignedViewProps = {
  clinicalTrial: ClinicalTrialType | null;
  data: VisitConfigQuestionnaires[];
  loading: boolean;
};
export const ClinicalTrialFormsAssignedView: FC<
  ClinicalTrialFormsAssignedViewProps
> = ({ data = [], loading, clinicalTrial }) => {
  const context = useContext(TemplateContext);
  const navigate = useNavigate();
  const id = useParams().id as string;
  const [expandedRows, toggleExpandedRows] = useClinicalTrialFormSlice(
    (state, actions) => [state.expandedRows?.[id] || [], actions.toggleExpandedRows]
  );
  const [templateForDelete, setTemplateForDelete] = useState<string | null>();

  const unassignClinicalTrialQuestionnaireAction = useQuestionnairesSlice(
    (_, actions) => actions.unassignClinicalTrialQuestionnaireAction
  );

  const isPM = useAuthSlice(s => s?.data?.selectedRole === UserRoles.PROJECT_MANAGER);

  const expandedRowRender = useCallback(
    transformExpandedVisit(
      uiControlSchemas[clinicalTrial?.status ?? StudyStatusEnum.draft].forms,
      questionnaireId => {
        setTemplateForDelete(questionnaireId);
      },
     isPM
    ),
    [loading, clinicalTrial]
  );
  return (
    <div data-testid='ClinicalTrialFormsAssignedView'>
      <div className='relative h-full'>
        <TableNew<TableRecord & VisitConfigQuestionnaires>
          locale={{
            emptyText: (
              <div className='absolute top-1/2 left-1/2 -translate-x-1/2 translate-y-1/2'>
                {!isPM && (<NoContent
                  action={() =>
                    navigate(routesPath.clinicalTrialVisits.replace(':id', id))
                  }
                  actionText={'Visit Schedule'}
                  iconName={'visit'}
                  title={
                    <span className='text-1424 text-spanishGray'>
                      {'No '}
                      <span className='font-semibold text-chineseBlack'>
                        Visits
                      </span>
                      {' configured here yet!'}
                      <br /> Add visits to configure forms
                    </span>
                  }
                />)}
              </div>
            ),
          }}
          columns={visitsColumns}
          dataSource={transformVisits(
            data,
            uiControlSchemas[clinicalTrial?.status ?? StudyStatusEnum.draft]
              .forms,
            (visit, templateType) => {
              context?.setCurrentVisit(visit);
              context?.setCurrentTemplateType(templateType);
            },
            isPM
          )}
          expandable={{
            expandedRowRender,
            expandedRowKeys: expandedRows,
            onExpand: (expanded, record) => {
              toggleExpandedRows({
                key: id,
                row: record.id,
                expanded
              });
            }
          }}
          pagination={false}
          rowKey={cohort => `${cohort.id}`}
          tableClassName={twMerge(
            'bg-dodgerBlue main-table mb-2 without-head',
            !data.length && !loading && 'no-content-transparent'
          )}
        />
      </div>
      <Modal
        width={440}
        onCancel={() => setTemplateForDelete(null)}
        onOk={() => {
          if (id && templateForDelete) {
            unassignClinicalTrialQuestionnaireAction({
              id,
              questionnaireId: templateForDelete,
            });
            setTemplateForDelete(null);
          }
        }}
        okText='Delete'
        centered
        cancelText='Cancel'
        maskClosable={false}
        open={!!templateForDelete}
        mask={false}
        okClassName='ant-btn-dangerous'
        classNames={{
          header: 'text-left',
          content: 'text-center !rounded-[2.4rem] !p-[4rem]',
          footer: 'flex justify-between',
        }}
        title='Delete Form'
        titleClassName='text-left'
      >
        Are you sure you want to delete this Form?
        <br />
        This action cannot be undone
      </Modal>
    </div>
  );
};
